import dayjs from "dayjs";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Select,
  SelectChangeEvent,
  Box,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/SearchBar";
import Pagination from "../../components/Panigation";
import { showError } from "../../helpers";
import { useLazyGetActivityLogListQuery } from "../../services/activityLog";
import {
  ACTIVITY_MODULES,
  ACTIVITY_TYPES,
  USER_TYPE,
} from "../../constants/enums";

interface ActivityLogUser {
  _id: string;
  email?: string;
  userType?: number;
  name?: string;
}

interface ActivityLogItem {
  _id: string;
  userId: ActivityLogUser | null;
  ipAddress?: string;
  userType?: number;
  module: number;
  activityType: number;
  details?: string;
  metaData?: Record<string, any>;
  hiddenFromUser: boolean;
  createdBy: string;
  isSystem: boolean;
  isDeleted: boolean;
  targetModel?: string;
  createdAt: string;
  updatedAt: string;
  __v?: number;
}

interface ActivityLogsResponse {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  logs: ActivityLogItem[];
}

const RewardAuditLogTable = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [Status, setStatus] = useState("default");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [activityLogs, setActivityLogs] = useState<ActivityLogItem[]>([]);
  const [getLogs] = useLazyGetActivityLogListQuery();

  const onPageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPage(newPage);
  };

  const formatEnumLabel = (label?: string) => {
    if (!label) return "-";
    return label
      .toLowerCase()
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const getActionLabel = (activityType?: number) => {
    if (activityType === undefined || activityType === null) return "-";
    const actionKey = Object.keys(ACTIVITY_TYPES).find(
      (key) =>
        ACTIVITY_TYPES[key as keyof typeof ACTIVITY_TYPES] === activityType
    );
    return actionKey ? formatEnumLabel(actionKey) : "-";
  };

  const getModuleName = (moduleValue: number): string => {
    const moduleKey = Object.keys(ACTIVITY_MODULES).find(
      (key) =>
        ACTIVITY_MODULES[key as keyof typeof ACTIVITY_MODULES] === moduleValue
    );
    return moduleKey ? formatEnumLabel(moduleKey) : `Module ${moduleValue}`;
  };

  const getUserRoleLabel = (type?: number) => {
    if (type === undefined || type === null) return "-";
    const enumKey = (USER_TYPE as any)[type];
    return enumKey ? formatEnumLabel(enumKey) : "-";
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return "-";
    const parsed = dayjs(dateString);
    return parsed.isValid() ? parsed.format("DD-MMM-YYYY hh:mm A") : "-";
  };

  const getEntityLabel = (log: ActivityLogItem) => {
    return (
      log.metaData?.rewardTitle ||
      log.metaData?.entityName ||
      log.targetModel ||
      "-"
    );
  };

  const handleNavigateToReward = (rewardId?: string) => {
    if (!rewardId) return;
    navigate(`/manage-rewards/detail/${rewardId}`);
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
  };

  const fetchLogsList = async () => {
    setLoading(true);
    try {
      const params: any = {
        page: page,
        limit: pageSize,
        search: debouncedSearchTerm,
        module:
          Status === "default" || Status === "all"
            ? ACTIVITY_MODULES.REWARD_AND_PROMOTION
            : Status,
      };
      if (Status === "default" || Status === "all") {
      }
      const response = (await getLogs(params).unwrap()) as any;
      if (response?.statusCode === 200 && response?.data) {
        setActivityLogs(response.data.logs || []);
        setTotalPages(response.data.totalPages || 0);
        setTotal(response.data.total || 0);
      }
    } catch (error: any) {
      showError(error?.data?.message || "Failed to fetch Logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    fetchLogsList();
  }, [page, pageSize, debouncedSearchTerm, Status]);

  const filteredLogs = useMemo(() => {
    if (Status === "default" || Status === "all") {
      return activityLogs;
    }
    return activityLogs.filter((log) => {
      const userTypeValue = log.userId?.userType ?? log.userType;
      return String(userTypeValue) === Status;
    });
  }, [Status, activityLogs]);

  return (
    <>
      <div className="table_header">
        <div className="left_s">
          <SearchBar
            value={searchTerm}
            onChange={handleSearchChange}
            onCross={handleClearSearch}
            searchTerm={searchTerm}
            setDebouncedSearchTerm={setDebouncedSearchTerm}
            placeholder="Search..."
          />
          <div className=" form">
            <Select
              fullWidth
              value={Status}
              onChange={(e: SelectChangeEvent) => setStatus(e.target.value)}
            >
              <MenuItem disabled value="default">
                Select Role
              </MenuItem>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value={`${USER_TYPE.APP}`}>
                {formatEnumLabel((USER_TYPE as any)[USER_TYPE.APP])}
              </MenuItem>
              <MenuItem value={`${USER_TYPE.ERP}`}>
                {formatEnumLabel((USER_TYPE as any)[USER_TYPE.ERP])}
              </MenuItem>
            </Select>
          </div>
        </div>
      </div>

      <TableContainer className="table_container">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Admin/User</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Details</TableCell>
              <TableCell>Entity</TableCell>
              <TableCell>Date & Time</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Loading activity logs...
                </TableCell>
              </TableRow>
            ) : filteredLogs.length ? (
              filteredLogs.map((log) => {
                const rewardId = log.metaData?.rewardId || log.metaData?._id;
                const displayName =
                  log.userId?.name ||
                  log.userId?.email?.split("@")[0] ||
                  "Unknown User";
                const userEmail = log.userId?.email || "-";
                const roleLabel = getUserRoleLabel(
                  log.userId?.userType ?? log.userType
                );

                return (
                  <TableRow hover key={log._id}>
                    {/* Admin Info */}
                    <TableCell>
                      <div className="user_block">
                        <figure>
                          <img
                            src="/static/images/user_placeholder.png"
                            alt="Images"
                          />
                        </figure>
                        <p>
                          <b>{displayName}</b>
                          <span>{userEmail}</span>
                        </p>
                      </div>
                    </TableCell>

                    {/* Role */}
                    <TableCell>{roleLabel}</TableCell>

                    {/* Action */}
                    <TableCell>{getModuleName(log.module)}</TableCell>

                    {/* Details */}
                    <TableCell>{log.details || "-"}</TableCell>

                    {/* Entity */}
                    <TableCell>
                      {rewardId ? (
                        <Box
                          component="button"
                          type="button"
                          className="click_action"
                          onClick={() => handleNavigateToReward(rewardId)}
                        >
                          <b>{getEntityLabel(log)}</b>
                        </Box>
                      ) : (
                        <span>{getEntityLabel(log)}</span>
                      )}
                    </TableCell>

                    {/* Date */}
                    <TableCell>
                      {formatDateTime(log.metaData?.timestamp || log.createdAt)}
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No audit logs found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {totalPages > 0 && (
        <Pagination
          module={filteredLogs}
          page={page}
          setPage={setPage}
          limit={pageSize}
          setLimit={setPageSize}
          onPageChange={onPageChange}
          totalPages={totalPages}
        />
      )}
    </>
  );
};

export default RewardAuditLogTable;
